/** Semáforo del listado: prioriza cobertura (mismo criterio que Info 360). */
export type SemaforoProductoListado = 'ok' | 'warning' | 'danger' | 'neutral';

export interface SemaforoProductoListadoResult {
  severidad: SemaforoProductoListado;
  titulo: string;
}

export function evaluarSemaforoProductoListado(producto: {
  cantidad?: number | null;
  stockMin?: number | null;
  tipoArticulo?: string | null;
  /** Si viene informado (aunque sea 0), el semáforo se define por cobertura. */
  coberturaDias?: number | null;
  demandaMensual6m?: number | null;
  leadTimeDias?: number | null;
}): SemaforoProductoListadoResult {
  const tipo = (producto.tipoArticulo ?? '').trim().toUpperCase();

  if (tipo === 'INA' || tipo === 'DIS') {
    return { severidad: 'neutral', titulo: 'Inactivo' };
  }

  if (tipo === 'FIN' || tipo === 'SER' || tipo === 'KIT') {
    return { severidad: 'neutral', titulo: 'Sin stock' };
  }

  const usaCobertura =
    producto.coberturaDias != null
    && Number.isFinite(Number(producto.coberturaDias))
    && producto.demandaMensual6m != null
    && Number.isFinite(Number(producto.demandaMensual6m));

  if (usaCobertura) {
    const coberturaDias = Number(producto.coberturaDias);
    const demandaMensual6m = Number(producto.demandaMensual6m);
    const leadTimeDias = Number(producto.leadTimeDias ?? 7) || 7;

    if (coberturaDias <= 0 && demandaMensual6m > 0) {
      return { severidad: 'danger', titulo: 'Sin cobertura' };
    }
    if (coberturaDias < leadTimeDias && demandaMensual6m > 0) {
      return { severidad: 'warning', titulo: 'Cobertura baja' };
    }
    if (coberturaDias <= 0) {
      return { severidad: 'neutral', titulo: 'Sin demanda' };
    }
    return { severidad: 'ok', titulo: 'Cobertura adecuada' };
  }

  // Fallback liviano si aún no llegó la cobertura del batch.
  const cantidad = producto.cantidad ?? 0;
  const stockMin = producto.stockMin ?? 0;

  if (cantidad < 0) {
    return { severidad: 'danger', titulo: 'Stock crítico' };
  }
  if (cantidad === 0) {
    if (stockMin > 0) {
      return { severidad: 'danger', titulo: 'Stock crítico' };
    }
    return { severidad: 'neutral', titulo: 'Sin stock · revisar' };
  }
  if (stockMin > 0 && cantidad < stockMin) {
    return { severidad: 'danger', titulo: 'Stock bajo mínimo' };
  }
  if (stockMin > 0 && cantidad < stockMin * 1.2) {
    return { severidad: 'warning', titulo: 'Requiere reposición' };
  }
  return { severidad: 'ok', titulo: 'Disponible' };
}

const BOTON_COLORES: Record<SemaforoProductoListado, string> = {
  danger: '#d32f2f',
  warning: '#ed6c02',
  ok: '#2e7d32',
  neutral: '#757575',
};

/** Estilo tipo Info 360 de clientes: ícono coloreado sin fondo. */
export function estilosBotonInfo360Listado(severidad: SemaforoProductoListado) {
  return {
    color: BOTON_COLORES[severidad],
    bgcolor: 'transparent',
    '&:hover': { bgcolor: 'action.hover' },
    ...(severidad === 'danger' ? {
      animation: 'info360ProductoRiesgo 1.4s ease-in-out infinite',
      '@keyframes info360ProductoRiesgo': {
        '0%, 100%': {
          transform: 'scale(1)',
          filter: 'drop-shadow(0 0 0 rgba(211,47,47,0))',
        },
        '50%': {
          transform: 'scale(1.18)',
          filter: 'drop-shadow(0 0 5px rgba(211,47,47,0.75))',
        },
      },
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    } : {}),
  };
}
